import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

// A button that opens a menu (the Create Groups menu's contract): click
// toggles; Enter, Space or ArrowDown open it on its first item, a mouse open
// focuses nothing until hover; arrows, Home and End move and wrap; Escape
// closes back onto the button; Tab, focus leaving, or a pointer press outside
// close it. Choosing an item closes the menu, then runs the item.
//
// The open state is the caller's when it passes `isOpen` and `onOpenChange`
// (so one of several menus can be open at a time), else the hook's own.

const FIRST_ITEM = 0;
const KEYS_THAT_CLICK = ["Enter", " "];

let nextMenuId = 0;

const navigationTarget = (key, activeIndex, itemCount) => {
  const last = itemCount - 1;
  const wrap = (index) => (index + itemCount) % itemCount;
  switch (key) {
    case "ArrowDown":
      return activeIndex === null ? FIRST_ITEM : wrap(activeIndex + 1);
    case "ArrowUp":
      return activeIndex === null ? last : wrap(activeIndex - 1);
    case "Home":
      return FIRST_ITEM;
    case "End":
      return last;
    default:
      return null;
  }
};

export const useMenuButton = ({ idPrefix, itemCount, onOpen, isOpen: controlledOpen, onOpenChange }) => {
  const menuId = useState(() => `${idPrefix}-${(nextMenuId += 1)}`)[0];
  const [ownOpen, setOwnOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : ownOpen;
  const setOpenRef = useRef(null);
  setOpenRef.current = isControlled ? onOpenChange : setOwnOpen;

  const [activeIndex, setActiveIndex] = useState(null);
  const [focusIndex, setFocusIndex] = useState(null);
  const [focusFromKeyboard, setFocusFromKeyboard] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const itemRefs = useRef([]);
  // Enter and Space reach the button as a click; remember they came from the
  // keyboard so the menu opens on its first item.
  const clickFromKeyboardRef = useRef(false);

  const close = useCallback(() => {
    setActiveIndex(null);
    setFocusFromKeyboard(false);
    setFocusIndex(null);
    setOpenRef.current(false);
  }, []);

  const open = (startIndex) => {
    setActiveIndex(null);
    setFocusFromKeyboard(startIndex !== null);
    setFocusIndex(startIndex);
    setOpenRef.current(true);
    onOpen?.();
  };

  useEffect(() => {
    if (isOpen && focusIndex !== null) itemRefs.current[focusIndex]?.focus();
  }, [isOpen, focusIndex]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsidePointer = (event) => {
      if (!wrapperRef.current?.contains(event.target)) close();
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [isOpen, close]);

  const handleButtonClick = () => {
    const fromKeyboard = clickFromKeyboardRef.current;
    clickFromKeyboardRef.current = false;
    if (isOpen) close();
    else open(fromKeyboard ? FIRST_ITEM : null);
  };

  // A mouse open leaves focus on the button, so Escape arrives here. It is
  // handled (and stopped) here, so a dialog around the menu stays open.
  const handleButtonKeyDown = (event) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (KEYS_THAT_CLICK.includes(event.key)) {
      clickFromKeyboardRef.current = true;
    } else if (event.key === "ArrowDown" && !isOpen) {
      event.preventDefault();
      open(FIRST_ITEM);
    }
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      buttonRef.current?.focus();
      return;
    }
    const target = navigationTarget(event.key, activeIndex, itemCount);
    if (target === null) return;
    event.preventDefault();
    setFocusFromKeyboard(true);
    itemRefs.current[target]?.focus();
  };

  // Focus leaving the button and its menu (Tab, or a click elsewhere that
  // takes focus) closes the menu without pulling focus back.
  const handleBlur = (event) => {
    if (isOpen && !event.currentTarget.contains(event.relatedTarget)) close();
  };

  const choose = (action) => {
    flushSync(close);
    action?.();
  };

  return {
    isOpen,
    buttonRef,
    wrapperProps: { ref: wrapperRef, onBlur: handleBlur },
    buttonProps: {
      ref: buttonRef,
      type: "button",
      "aria-haspopup": "menu",
      "aria-expanded": isOpen ? "true" : "false",
      "aria-controls": isOpen ? menuId : undefined,
      onClick: handleButtonClick,
      onKeyDown: handleButtonKeyDown,
      onPointerDown: () => { clickFromKeyboardRef.current = false; },
    },
    menuProps: { id: menuId, role: "menu", onKeyDown: handleMenuKeyDown },
    itemProps: (index, action) => ({
      ref: (el) => { itemRefs.current[index] = el; },
      type: "button",
      role: "menuitem",
      tabIndex: -1,
      $active: activeIndex === index,
      "data-active": activeIndex === index ? "true" : "false",
      $keyboardFocus: focusFromKeyboard && activeIndex === index,
      onMouseEnter: () => {
        setFocusFromKeyboard(false);
        itemRefs.current[index]?.focus();
      },
      onFocus: () => setActiveIndex(index),
      onClick: () => choose(action),
    }),
  };
};
